import { CircularProgress } from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import * as Yup from 'yup';
import TextInput from "../common/textfield/TextInput";
import SelectField from "../common/textfield/SelectField";
import TextArea from "../common/textfield/TextArea";
import Radio from "../common/textfield/Radio";
import { Button } from "../common/Button";
import { joinWaitingRoom } from "../api/WaitingRoom";
import { createQuestionnaire, updateQuestionnaire } from "../api/Questionnaires";
import { failed, success } from "../common/Toastify";
import { useSelector } from "react-redux";
import moment from "moment";
import { getPatientById } from "../api/Individual";
import { decryptData } from "../EncryptDecrypt";
import { regions } from "./Constants";

const getPatientDisplayName = (data) => {
    if (!data) return '';
    const firstName = data.firstName ? decryptData(data.firstName) : '';
    const lastName = data.lastName ? decryptData(data.lastName) : '';
    return [firstName, lastName].filter(Boolean).join(' ').trim();
};

const buildQuestionResponsePayload = (questions, values) => {
    return questions?.item?.map((question) => {
        const answerValue = values[question.text] ?? '';
        const isDropdown = question.answerType === 'dropDown';
        const isRadio = question.answerType === 'radio';
        let responseAnswer = answerValue;

        if (question.tag === 'TELEPHONE' && values.callerNumberExt) {
            responseAnswer = `${answerValue} ext ${values.callerNumberExt}`;
        }

        return {
            id: question.linkId,
            question: question.text,
            questionType: question.answerType,
            tag: question.tag,
            answer: (question.answerOption || []).map((option) => ({
                ...option,
                answer: isRadio
                    ? option.text === answerValue
                    : isDropdown
                        ? option.text === answerValue
                        : responseAnswer,
                required: question.required,
                option: isRadio || isDropdown ? option.text : null,
                value: isDropdown ? option.text : null,
                name: option.text,
            })),
        };
    }) || [];
};

const buildIntakeQuestions = (questions, values) => {
    const intakeQuestions = {};

    questions?.item?.forEach((question) => {
        const answerValue = values[question.text] ?? '';
        const tag = question.tag;

        if (tag === 'TELEPHONE') {
            intakeQuestions.callerNumber = answerValue;
            if (values.callerNumberExt) {
                intakeQuestions.callerNumberExt = values.callerNumberExt;
            }
            return;
        }

        if (tag === 'LOCATION') {
            const locationObj = regions.find((region) => region.name === answerValue);
            intakeQuestions.callerLocation = locationObj ? locationObj.value : answerValue;
            return;
        }

        if (tag === 'CHANNEL') {
            intakeQuestions.preferredChannel = answerValue;
            return;
        }

        if (tag === 'REASON') {
            intakeQuestions.reason = answerValue;
            return;
        }

        if (tag) {
            intakeQuestions[tag.toLowerCase()] = answerValue;
        }

        intakeQuestions[question.text] = answerValue;
    });

    return intakeQuestions;
};

const getEncounterClass = (preferredChannel) => (
    String(preferredChannel || '').trim().toLowerCase() === 'chat' ? 'chatback-rn' : 'callback-rn'
);

const QuestionnaireWaitingRoom = ({ questionnaireWRModalShow, handleQuestionnaireWRModalClose, WRquestions, openWRScreen, orgName }) => {
    const [isLoading, setIsLoading] = useState(false);
    const storedProfile = sessionStorage.getItem("relatedPersonProfile");
    const storedId = storedProfile ? JSON.parse(storedProfile).id : null;

    const patientRecordId = useSelector((state) => storedId || state?.auth?.user?.["custom:patient_id"]);
    const orgId = useSelector((state) => state?.auth?.user?.orgId || state?.auth?.user?.["custom:orgId"]);
    const [patientName, setPatientName] = useState("");
    const user = useSelector((state) => state?.auth?.user);

    useEffect(() => {
        if (!patientRecordId || patientName) return;

        getPatientById(patientRecordId)
            .then((res) => {
                if (res?.status === true) {
                    setPatientName(getPatientDisplayName(res?.data) || user?.email || '');
                }
            })
            .catch(() => {
                setPatientName(user?.email || '');
            });
    }, [patientRecordId, patientName, user?.email]);



    const formik = useFormik({
        initialValues: (() => {
            const initialValues = {
                callerNumberExt: ""
            };

            WRquestions?.item?.forEach((question) => {
                switch (question.answerType) {
                    case 'freeText':
                    case 'dropDown':
                        initialValues[question.text] = '';
                        break;
                    case 'radio':
                        initialValues[question.text] = question.answerOption?.[0]?.text || '';
                        break;
                    default:
                        initialValues[question.text] = '';
                }
            });

            return initialValues;
        })(),
        validationSchema: Yup.object().shape({
            ...WRquestions?.item?.reduce((validationSchema, question) => {
                if (question.required) {
                    validationSchema[question.text] = Yup.string().required(`${question.text} is required`);
                }

                if (question.tag === 'TELEPHONE') {
                    validationSchema[question.text] = Yup.string()
                        .matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Invalid phone number. Please enter a valid format like (123) 456-7890')
                        .required(`${question.text} is required`);
                }
                return validationSchema;
            }, {}),
        }),
        onSubmit: async (values) => {
            if (!patientRecordId || !orgId) {
                failed('Unable to start waiting room request. Please sign in again and retry.');
                return;
            }

            const displayName = patientName || user?.email || 'Patient';
            const startTime = moment().utc().format();
            const intakeQuestions = buildIntakeQuestions(WRquestions, values);
            const locationQuestion = WRquestions?.item?.find((question) => question.tag === 'LOCATION');
            const patientLocation = intakeQuestions.callerLocation
                || regions.find((region) => region.name === values[locationQuestion?.text])?.value
                || '';
            const reasonQuestion = WRquestions?.item?.find((question) => question.tag === 'REASON');

            setIsLoading(true);

            try {
                let questionnaireResponseResult = null;
                const questionResponse = buildQuestionResponsePayload(WRquestions, values);

                if (WRquestions?.id && questionResponse.length) {
                    questionnaireResponseResult = await createQuestionnaire({
                        patientID: patientRecordId,
                        questionResponse,
                        questionnaire: WRquestions.id,
                        authorName: displayName,
                        authorType: 'Patient',
                        authorId: patientRecordId,
                    });
                }

                if (questionnaireResponseResult?.data?.id) {
                    intakeQuestions.questionnaireResponseId = {
                        reference: `QuestionnaireResponse/${questionnaireResponseResult.data.id}`,
                    };
                }

                const request = {
                    status: 'arrived',
                    patientID: patientRecordId,
                    class: getEncounterClass(intakeQuestions.preferredChannel),
                    statusHistory: [
                        {
                            status: 'arrived',
                            actor: {
                                reference: `Patient/${patientRecordId}`,
                                display: displayName,
                                type: 'Patient',
                            },
                            period: {
                                start: startTime,
                                end: null,
                            },
                        },
                    ],
                    period: {
                        start: startTime,
                        end: null,
                    },
                    subject: {
                        reference: `Patient/${patientRecordId}`,
                        type: 'Patient',
                        display: displayName,
                    },
                    participant: [
                        {
                            individual: {
                                reference: `Patient/${patientRecordId}`,
                                type: 'Patient',
                                display: displayName,
                            },
                            period: {
                                start: startTime,
                                end: null,
                            },
                        },
                    ],
                    patientLocation,
                    description: intakeQuestions.reason || values[reasonQuestion?.text] || '',
                    intakeQuestions,
                    serviceProvider: {
                        reference: `Organization/${orgId}`,
                        type: 'Organization',
                        display: orgName,
                    },
                };

                const encounterResponse = await joinWaitingRoom(request);
                const encounterId = encounterResponse?.data?.id;

                if (!encounterId) {
                    failed(encounterResponse?.message || 'Unable to create waiting room encounter.');
                    return;
                }

                if (questionnaireResponseResult?.data?.id) {
                    await updateQuestionnaire({
                        encounterId,
                        patientID: patientRecordId,
                        authorId: patientRecordId,
                        authorType: 'Patient',
                        authorName: displayName,
                    }, questionnaireResponseResult.data.id);
                }

                success(encounterResponse?.message || 'Waiting room request sent.');
                handleQuestionnaireWRModalClose();
                openWRScreen({ encounterId });
            } catch (error) {
                failed(
                    error?.response?.data?.message
                    || error?.response?.data?.error
                    || error?.message
                    || 'Unable to enter the waiting room.'
                );
            } finally {
                setIsLoading(false);
            }
        }
    });

    const handleRadioChange = (question, value) => {
        formik.setFieldValue(question, value);
    };

    return (
        <section>
            {isLoading ? (
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "80vh"
                }}>
                    <CircularProgress size={50} />
                </div>
            ) : (
                <Modal
                    backdropClassName
                    backdrop={'static'}
                    show={questionnaireWRModalShow} onHide={handleQuestionnaireWRModalClose}
                    aria-labelledby="contained-modal-title-vcenter"
                    centered className="custom-dialog">

                    <Modal.Header closeButton className="border-0">
                        <Modal.Title id="contained-modal-title-vcenter">Questions</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>

                        <form className="common-form border-fields" onSubmit={formik.handleSubmit}>

                            {WRquestions?.item?.map((question, questionIndex) => (
                                <div key={questionIndex}>
                                    {question.answerType === 'freeText' && question.tag !== 'TELEPHONE' && (
                                        <TextArea
                                            keyField={question.text}
                                            label={question.text}
                                            formik={formik}
                                            
                                            placeholder={"Type your answer here"}
                                        />
                                    )}

                                    {question.answerType === 'freeText' && question.tag === 'TELEPHONE' && (
                                        <Row>

                                            <Col>
                                                <TextInput
                                                    keyField={question.text}
                                                    label={'Return Telephone Number'}
                                                    formik={formik}
                                                    type="phone"
                                                    placeholder={"Contact Number here"}
                                                />
                                            </Col>

                                            <Col>
                                                <TextInput keyField={"callerNumberExt"} type="ext" label={"Ext."} formik={formik} placeholder={"Ext"} required={false} />
                                            </Col>

                                        </Row>

                                    )}

                                    {question.answerType === 'dropDown' && (
                                        <SelectField
                                            keyField={question.text}
                                            label={question.text}
                                            formik={formik}
                                            options={question.answerOption.map(option => ({ value: option.text, name: option.text }))}
                                        />
                                    )}

                                    {question.answerType === 'radio' && (
                                        <div>
                                            <div>{question.text}</div>
                                            {question.answerOption?.map((option, optionIndex) => (
                                                <Radio
                                                    key={optionIndex}
                                                    keyField={option.text}
                                                    name={question.text}
                                                    value={option.text}
                                                    radioLabel={option.text}
                                                    label={option.text}
                                                    isChecked={formik?.values[question.text] === option.text}
                                                    formik={formik}
                                                    handleChange={(value) => handleRadioChange(question.text, value)}
                                                />
                                            ))}
                                        </div>
                                    )}


                                </div>
                            ))}

                            <Button type="submit" disabled={isLoading}>
                                {isLoading ? <CircularProgress size={24} /> : 'Enter Waiting Room'}
                            </Button>
                        </form>

                    </Modal.Body>
                </Modal>
            )}
        </section>
    )
}

export default QuestionnaireWaitingRoom;